import type { ComponentPropsWithoutRef, ReactNode } from "react";

import { cn } from "@/lib/utils";
import { NamespaceProviderWarning } from "@/components/workspaces/namespace-provider-warning";

import {
  WorkspaceContentContainer,
  type WorkspaceContentContainerProps,
} from "@/components/workspaces/workspace-content-container";
import { WorkspaceContent, WorkspaceLayout } from "@/components/workspaces/workspace-layout";
import { WorkspaceTabRailPositionProvider } from "@/components/workspaces/workspace-tab-rail-position-context";

type LayoutProps = ComponentPropsWithoutRef<typeof WorkspaceLayout>;
type ContentProps = ComponentPropsWithoutRef<typeof WorkspaceContent>;

export interface WorkspaceCanvasProps extends Omit<LayoutProps, "children"> {
  /**
   * Optional mission-level chrome rendered above the workspace content. Typically a TopBar.
   */
  topbar?: ReactNode;

  /**
   * Optional workspace rail (tabs, filters, etc.) rendered between the topbar and panels.
   */
  workspaceTopbar?: ReactNode;

  /**
   * Overrides for the flex column that wraps the topbar, rail, and panels.
   */
  chromeClassName?: string;

  /**
   * Max-width utility applied to the chrome wrapper. Defaults to 1600px.
   */
  maxWidthClassName?: string;

  /**
   * When true, the chrome wrapper stretches edge-to-edge instead of centering within a max-width.
   */
  fullBleed?: boolean;

  /**
   * Props forwarded to the underlying WorkspaceContent wrapper.
   */
  contentProps?: Omit<ContentProps, "children">;

  /**
   * Props forwarded to the WorkspaceContentContainer wrapper when a workspaceTopbar
   * is provided. Use this to override padding, rounding, or other structural styles
   * around the canvas region while keeping the built-in rail wiring.
   */
  contentContainerProps?: Omit<WorkspaceContentContainerProps, "children" | "tabBar">;

  children: ReactNode;
}

export function WorkspaceCanvas({
  topbar,
  workspaceTopbar,
  chromeClassName,
  maxWidthClassName = "max-w-none",
  fullBleed = false,
  contentProps,
  contentContainerProps,
  children,
  className,
  style,
  fullHeight: layoutFullHeight = true,
  ...layoutProps
}: WorkspaceCanvasProps) {
  const {
    className: contentClassName,
    fullHeight: contentFullHeight = layoutFullHeight,
    ...restContentProps
  } = contentProps ?? {};

  const {
    className: containerClassName,
    tabBarClassName: containerTabBarClassName,
    workspaceClassName: containerWorkspaceClassName,
    innerClassName: containerInnerClassName,
    ...restContentContainerProps
  } = contentContainerProps ?? {};
  const showTopbar = Boolean(topbar);
  const showWorkspaceRail = Boolean(workspaceTopbar);

  const workspacePanels = (
    <WorkspaceContent
      {...restContentProps}
      fullHeight={contentFullHeight}
      className={cn(
        "workspace-panels",
        showWorkspaceRail
          ? "mt-0"
          : showTopbar
            ? "mt-[var(--space-lg)]"
            : undefined,
        contentClassName,
      )}
    >
      {children}
    </WorkspaceContent>
  );

  const stackedPanels = showWorkspaceRail ? (
    <WorkspaceContentContainer
      tabBar={workspaceTopbar}
      tabBarClassName={cn(
        "w-full min-w-0",
        showTopbar && "mt-[var(--space-sm)]",
        containerTabBarClassName,
      )}
      className={containerClassName}
      workspaceClassName={containerWorkspaceClassName}
      innerClassName={containerInnerClassName}
      {...restContentContainerProps}
    >
      {workspacePanels}
    </WorkspaceContentContainer>
  ) : (
    workspacePanels
  );

  return (
    <WorkspaceTabRailPositionProvider>
      <WorkspaceLayout
        {...layoutProps}
        fullHeight={layoutFullHeight}
        className={cn(
          "workspace-canvas relative isolate w-full p-[var(--space-sm)] text-foreground",
          className,
        )}
        style={{
          // Background lives on the document body so the gradient continues
          // edge-to-edge under iOS notch / Android cutout; the canvas itself
          // stays transparent and only handles the safe-area-aware padding so
          // UI content stays clear of system chrome.
          paddingTop: "max(var(--space-sm), env(safe-area-inset-top))",
          paddingBottom: "max(var(--space-sm), env(safe-area-inset-bottom))",
          paddingLeft: "max(var(--space-sm), env(safe-area-inset-left))",
          paddingRight: "max(var(--space-sm), env(safe-area-inset-right))",
          ...style,
        }}
      >
        <div
          className={cn(
            "workspace-chrome flex w-full flex-1 min-h-0 flex-col overflow-hidden",
            fullBleed ? "mx-0 max-w-none" : "mx-auto",
            fullBleed ? undefined : maxWidthClassName,
            chromeClassName,
          )}
        >
          {showTopbar ? <div className="workspace-topbar">{topbar}</div> : null}
          <NamespaceProviderWarning />
          {stackedPanels}
        </div>
      </WorkspaceLayout>
    </WorkspaceTabRailPositionProvider>
  );
}
